'use client';

import { useEffect, useState, useRef } from 'react';
import { Tables } from '@/types/supabase';
import { formatDuration } from '@/lib/utils';
import { useNameFormatting } from '@/contexts/NameFormattingContext';
import { Badge } from '@/components/ui/badge';
import { useTranslations, useLocale } from 'next-intl';
import { generatePublicUrl } from '@/models/eventModel';
import { Link } from '@/components/ui';
import { createClient } from '@/lib/supabase/client';
import type {
  FutsalScoreAppState,
  FutsalAction,
  FutsalMatchConfig,
  StandingPointsBreakdown,
  StandingPointsLineItem,
} from '@/app/[locale]/match/score/components/futsal-types';
import { calculateStandingPoints } from '@/lib/futsal-standings';
import { isFoulAction } from '@/app/[locale]/match/score/components/futsal-reducers';
import {
  getPlayerColorHex,
  getPlayerColorClasses,
  type PlayerColor,
} from '@/app/[locale]/match/score/components/colorUtils';
import PlayerIcon from '@/app/[locale]/match/score/components/PlayerIcon';
import { rosterInline, type TeamEntry } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { resolveParticipantLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import type { ScorecardPlayerName } from '../../libs/scorecardPlayer';

type Match = Tables<'match'> & {
  match_game?: Array<
    Tables<'match_game'> & {
      match_game_point?: Tables<'match_game_point'>[];
    }
  >;
};

type Player = ScorecardPlayerName;

interface FutsalScorecardClientProps {
  match: Match;
  /** The verified viewer scored this match. */
  canScoreAgain: boolean;
  player1: Player;
  player2: Player;
  /**
   * Team-division entry for each side (`event_group.is_team = true`). The big
   * home/away label (`config.homeName`/`awayName`) already carries the team name
   * — it's set from the lead player's team label when the match is created (see
   * `score/[matchId]/page.tsx`). Matches created before that seeding keep the
   * personal name as the headline (app_state is not rewritten). These are used
   * only to swap the small secondary line under it from the lead player's
   * personal name to the roster.
   */
  player1Team?: TeamEntry | null;
  player2Team?: TeamEntry | null;
  eventInfo: {
    event_type: string;
    event_id: string | null;
    event_name: string | null;
    event_location_name: string | null;
    event_division_name: string | null;
  } | null;
  eventSlug: string | null;
}

function formatTimerShort(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getActionDescription(action: FutsalAction, t: ReturnType<typeof useTranslations>): string {
  switch (action.type) {
    case 'goal':
      return action.isCaptainGoal ? t('scoring.actionCaptainGoal') : t('scoring.actionGoal');
    case 'foul':
      return t('scoring.actionFoul');
    case 'penalty_goal':
      return action.isCaptainGoal
        ? `${t('scoring.actionPenaltyGoal')} (C)`
        : t('scoring.actionPenaltyGoal');
    case 'penalty_saved':
      return t('scoring.actionPenaltySaved');
    case 'penalty_missed':
      return t('scoring.actionPenaltyMissed');
    case 'direct_foul':
      return t('scoring.actionDirectFoul');
    case 'kick_off':
      return t('scoring.actionKickOff');
  }
}

function getActionEmoji(type: FutsalAction['type']): string {
  switch (type) {
    case 'goal':
      return '\u26BD';
    case 'foul':
      return '\uD83D\uDFE1';
    case 'direct_foul':
      return '\u26A1';
    case 'penalty_goal':
      return '\u26BD';
    case 'penalty_saved':
      return '\uD83E\uDDE4';
    case 'penalty_missed':
      return '\u274C';
    case 'kick_off':
      return '\uD83C\uDFC1';
  }
}

function getTotalPeriods(format: FutsalMatchConfig['format']): number {
  return format === '3x5' ? 3 : 1;
}

function StandingPointsCard({
  name,
  color,
  items,
  total,
  t,
}: {
  name: string;
  color: PlayerColor;
  items: StandingPointsLineItem[];
  total: number;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className="border-border overflow-hidden rounded-xl border">
      {/* Header with team color accent */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ backgroundColor: `${getPlayerColorHex(color)}15` }}
      >
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: getPlayerColorHex(color) }}
          />
          <span className="text-foreground text-sm font-bold">{name}</span>
        </div>
        <span className="text-foreground text-2xl font-bold tabular-nums">{total}</span>
      </div>

      {/* Items */}
      {items.length > 0 && (
        <div className="divide-border divide-y px-4">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <div className="flex flex-col">
                <span className="text-foreground text-sm font-semibold">{t(item.ruleKey)}</span>
                <span className="text-muted text-xs">{t(item.reasonKey, item.reasonParams)}</span>
              </div>
              <span className="text-accent text-sm font-bold">+{item.points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FutsalScorecardClient({
  match: initialMatch,
  player1,
  player2,
  player1Team,
  player2Team,
  eventInfo,
  eventSlug,
  canScoreAgain,
}: FutsalScorecardClientProps) {
  const t = useTranslations('FutsalScorer');
  const tCard = useTranslations('Scorecard');
  const locale = useLocale();
  const { isJapanese } = useNameFormatting();
  const [match, setMatch] = useState(initialMatch);
  const [showAllActions, setShowAllActions] = useState(false);
  const isCompleted = match.status === 'completed';
  const isLive = match.status === 'in_progress';

  // Parse futsal state from app_state
  const appState = match.app_state as FutsalScoreAppState | null;
  const futsalState = appState?.futsalState;

  // Supabase Realtime subscription + 30s safety-net poll for live matches
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  useEffect(() => {
    if (isCompleted) return;

    const refreshMatch = async () => {
      try {
        const response = await fetch(`/api/match/${match.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data.match) {
            setMatch(data.match);
          }
        }
      } catch (error) {
        console.error('Error refreshing match data:', error);
      }
    };

    const supabase = createClient();
    const channel = supabase
      .channel(`scorecard-match-${match.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'match',
          filter: `id=eq.${match.id}`,
        },
        (payload) => {
          if (payload.new) {
            setMatch(payload.new as Match);
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    const interval = setInterval(refreshMatch, 30000);

    return () => {
      clearInterval(interval);
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [match.id, isCompleted]);

  if (!futsalState) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <p className="text-muted text-center">No match data available.</p>
      </div>
    );
  }

  const { config, actions, homeScore, awayScore } = futsalState;

  // Calculate standing points from app_state or compute on the fly
  const standingPoints: StandingPointsBreakdown =
    futsalState.standingPoints ?? calculateStandingPoints(actions);

  // Calculate match duration
  const matchStartTime = new Date(match.played_at);
  const matchEndTime = match.updated_at ? new Date(match.updated_at) : new Date();
  const durationSeconds = (matchEndTime.getTime() - matchStartTime.getTime()) / 1000;

  // Match result
  const homeWins = homeScore > awayScore;
  const isDraw = homeScore === awayScore;

  // Format label
  const totalPeriods = getTotalPeriods(config.format);
  const formatLabel = config.format === '3x5' ? '3x5min' : '1x10min';

  // Filter meaningful actions for timeline (exclude kick_off), most recent first
  const meaningfulActions = actions.filter((a) => a.type !== 'kick_off');
  const reversedActions = [...meaningfulActions].reverse();

  const PREVIEW_COUNT = 5;
  const hasMore = reversedActions.length > PREVIEW_COUNT;
  const displayedActions = showAllActions
    ? reversedActions
    : reversedActions.slice(0, PREVIEW_COUNT);

  // Per-period summary
  const periodSummaries = Array.from({ length: totalPeriods }, (_, i) => {
    const period = i + 1;
    const periodActions = actions.filter((a) => a.period === period);
    const homeGoals = periodActions.filter(
      (a) => (a.type === 'goal' || a.type === 'penalty_goal') && a.team === 'home',
    ).length;
    const awayGoals = periodActions.filter(
      (a) => (a.type === 'goal' || a.type === 'penalty_goal') && a.team === 'away',
    ).length;
    const homeFouls = periodActions.filter((a) => isFoulAction(a) && a.team === 'home').length;
    const awayFouls = periodActions.filter((a) => isFoulAction(a) && a.team === 'away').length;
    return { period, homeGoals, awayGoals, homeFouls, awayFouls };
  });

  // Secondary line under each team name: the roster when there is one, else
  // the lead player's set alias, else their personal name (player1 = home,
  // player2 = away). `entry: null` here — the team name itself is already the
  // headline (`config.homeName`/`awayName`); this line only ever falls
  // through to the alias/personal-name rungs of the ladder.
  const player1Roster = rosterInline(player1Team?.roster);
  const player2Roster = rosterInline(player2Team?.roster);
  const player1Name =
    player1Roster ?? resolveParticipantLabel({ entry: null, player: player1, isJapanese });
  const player2Name =
    player2Roster ?? resolveParticipantLabel({ entry: null, player: player2, isJapanese });

  return (
    <div className="mx-auto w-full max-w-6xl">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between sm:mb-8">
        <div>
          <h1 className="text-foreground text-xl font-bold sm:text-3xl">{tCard('title')}</h1>
        </div>
        {isCompleted && canScoreAgain && (
          <Link
            href={
              eventInfo?.event_id
                ? `/match/score-choice/event/${eventInfo.event_id}/match`
                : `/match/score-choice`
            }
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors sm:px-4 sm:py-2 sm:text-sm"
          >
            {tCard('scoreAgain')}
          </Link>
        )}
      </div>

      {/* Match Summary */}
      <div className="border-border bg-card mb-4 rounded-lg border p-4 shadow-sm sm:mb-8 sm:p-6">
        {/* Winner / Draw label */}
        <div className="mb-4 text-center">
          {isDraw ? (
            <span className="bg-foreground/10 text-foreground rounded-full px-4 py-1 text-sm font-semibold">
              {t('summary.draw')}
            </span>
          ) : (
            <span className="bg-accent/10 text-accent rounded-full px-4 py-1 text-sm font-semibold">
              {homeWins ? config.homeName : config.awayName} — {t('summary.winner')}
            </span>
          )}
        </div>

        {/* Score with team avatars */}
        <div className="mb-4 flex items-center justify-center gap-4 sm:mb-6 sm:gap-8">
          {/* Home team */}
          <div className="flex flex-col items-center gap-2">
            <PlayerIcon size="lg" color={getPlayerColorClasses(config.homeColor)} />
            <span className="text-foreground max-w-[7rem] truncate text-sm font-semibold">
              {config.homeName}
            </span>
            <span className="text-muted text-xs">{player1Name}</span>
          </div>

          {/* Score */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3">
              <span className="text-foreground text-5xl font-bold tabular-nums sm:text-6xl">
                {homeScore}
              </span>
              <span className="text-muted text-2xl font-light sm:text-3xl">–</span>
              <span className="text-foreground text-5xl font-bold tabular-nums sm:text-6xl">
                {awayScore}
              </span>
            </div>
            <Badge
              variant={isCompleted ? 'success' : 'destructive'}
              className={`mt-2 px-1.5 py-0.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm ${isLive ? 'animate-pulse' : ''}`}
            >
              {isCompleted ? tCard('matchCompleted') : tCard('matchInProgress')}
            </Badge>
          </div>

          {/* Away team */}
          <div className="flex flex-col items-center gap-2">
            <PlayerIcon size="lg" color={getPlayerColorClasses(config.awayColor)} />
            <span className="text-foreground max-w-[7rem] truncate text-sm font-semibold">
              {config.awayName}
            </span>
            <span className="text-muted text-xs">{player2Name}</span>
          </div>
        </div>

        {/* Event Information */}
        {eventInfo &&
          (eventInfo.event_name ||
            eventInfo.event_location_name ||
            eventInfo.event_division_name) && (
            <div className="border-border bg-surface mb-6 rounded-lg border p-4">
              <h3 className="text-foreground mb-3 font-semibold">{tCard('eventDetails')}</h3>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                {eventInfo.event_name && (
                  <div className="inline-flex items-center space-x-2">
                    <label className="text-muted">{tCard('eventName')}</label>
                    <div className="text-foreground text-left font-medium">
                      {eventInfo.event_id && eventSlug ? (
                        <Link
                          href={generatePublicUrl(eventInfo.event_id, eventSlug, locale)}
                          className="text-primary hover:text-primary/80 hover:underline"
                        >
                          {eventInfo.event_name}
                        </Link>
                      ) : (
                        eventInfo.event_name
                      )}
                    </div>
                  </div>
                )}
                {eventInfo.event_location_name && (
                  <div className="inline-flex items-center space-x-2">
                    <label className="text-muted">{tCard('location')}</label>
                    <div className="text-foreground text-left font-medium">
                      {eventInfo.event_location_name}
                    </div>
                  </div>
                )}
                {eventInfo.event_division_name && (
                  <div className="inline-flex items-center space-x-2">
                    <label className="text-muted">{tCard('division')}</label>
                    <div className="text-foreground text-left font-medium">
                      {eventInfo.event_division_name}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Match Details */}
        <div className="text-muted grid w-full grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div className="inline-flex items-center space-x-2">
            <label className="text-muted">{t('scorecard.matchDuration')}</label>
            <div className="text-left">{formatDuration(durationSeconds)}</div>
          </div>
          <div className="text-left sm:text-center">
            {t('scorecard.format', { format: formatLabel })}
          </div>
        </div>
      </div>

      {/* Period Breakdown */}
      {totalPeriods > 1 && (
        <div className="border-border bg-card mb-4 rounded-lg border p-4 shadow-sm sm:mb-8 sm:p-6">
          <h2 className="text-foreground mb-3 font-semibold">{t('summary.periodBreakdown')}</h2>
          <div className="space-y-2">
            {periodSummaries.map((ps) => (
              <div
                key={ps.period}
                className="border-border flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
              >
                <span className="text-muted font-medium">
                  {t('summary.period', { period: ps.period })}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-foreground font-bold tabular-nums">
                    {ps.homeGoals} – {ps.awayGoals}
                  </span>
                  <span className="text-muted text-xs">
                    {t('scoring.fouls')}: {ps.homeFouls} / {ps.awayFouls}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Standing Points — only after match completes */}
      {isCompleted && (
        <div className="border-border bg-card mb-4 rounded-lg border p-4 shadow-sm sm:mb-8 sm:p-6">
          <h2 className="text-foreground mb-3 font-semibold">{t('summary.standingPoints')}</h2>
          <div className="space-y-3">
            <StandingPointsCard
              name={config.homeName}
              color={config.homeColor}
              items={standingPoints.home.items}
              total={standingPoints.home.total}
              t={t}
            />
            <StandingPointsCard
              name={config.awayName}
              color={config.awayColor}
              items={standingPoints.away.items}
              total={standingPoints.away.total}
              t={t}
            />
          </div>
        </div>
      )}

      {/* Timeline */}
      {reversedActions.length > 0 && (
        <div className="border-border bg-card rounded-lg border p-4 shadow-sm sm:p-6">
          <h2 className="text-foreground mb-3 font-semibold">{t('scorecard.timeline')}</h2>
          <ul className="divide-border divide-y">
            {displayedActions.map((action) => {
              const isHome = action.team === 'home';
              const teamName = isHome ? config.homeName : config.awayName;
              const teamColor = isHome ? config.homeColor : config.awayColor;
              return (
                <li key={action.id} className="flex items-center gap-2 py-2.5 text-xs sm:text-sm">
                  <span className="text-base leading-none">{getActionEmoji(action.type)}</span>
                  <span
                    className="h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: getPlayerColorHex(teamColor) }}
                  />
                  <span className="text-foreground flex-1 font-medium">
                    {getActionDescription(action, t)}
                  </span>
                  <span className="font-medium" style={{ color: getPlayerColorHex(teamColor) }}>
                    {teamName}
                  </span>
                  <span className="text-muted font-mono">
                    {formatTimerShort(action.timerValue)}
                  </span>
                  {totalPeriods > 1 && (
                    <span className="bg-surface text-muted rounded px-1 py-0.5 text-[10px]">
                      P{action.period}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {hasMore && !showAllActions && (
            <button
              onClick={() => setShowAllActions(true)}
              className="text-primary hover:text-primary/80 mt-3 w-full text-center text-sm font-medium transition-colors"
            >
              {t('scorecard.viewAll', { count: reversedActions.length })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
